(ns io.daskul.webapp.widgets.fsm.colors
  "FSM variant to UI color mappings, and the state-badge palette.")

(def variant->color
  {

"info"      "blue"
   "success"   "green"
   "warning"   "yellow"
   "danger"    "red"
   "secondary" "gray"
   "neutral"   "gray"})

(defn get-color [variant]
  (get variant->color variant "brand"))

(def variant->button-props
  {"success"   {}  ;; uses brand/primary color (green feels disconnected for buttons)
   "danger"    {:color "red"}
   "warning"   {:color "yellow"}
   "secondary" {:variant "light" :color "gray"}
   "info"      {:color "blue"}
   "neutral"   {:variant "light" :color "gray"}})

(defn get-button-props [variant]
  (get variant->button-props variant {}))

;; ============================================================================
;; State badge palette (#634)
;; ============================================================================
;;
;; State badges used to render through Mantine's `light` variant: mid-tone text
;; on a 10%-alpha wash of the same hue. Measured on the leads list at 11px:
;;
;;   Lost (danger)         2.93:1
;;   Contacted (secondary) 3.02:1
;;   New (info)            3.17:1
;;
;; WCAG AA wants 4.5:1 for text that size, so every state badge in both
;; products failed. These pairs are solid backgrounds with darker text, and
;; `colors-test` asserts the floor rather than trusting the table.
;;
;; Keyed by the state's TONE — its position in the lifecycle — rather than its
;; :variant, because :variant is overloaded: it also drives button styling,
;; where `danger` really does mean red. On a badge, `danger` marks the unhappy
;; *ending* (Lost), and a red badge on every lost record reads as an error the
;; reader must fix.
;;
;; This key was called :role until #679. The concept was right; the word was
;; taken three times over — user role (::auth.subs/role → "school-admin"), the
;; ARIA attribute (getByRole all through the UI and the e2e suite), and this.
;; :tone collides with neither.

;; These are explicit values rather than Mantine shade tokens, and that was
;; measured, not assumed. Pairing each hue's shade 1 with its shade 9 — the
;; obvious "just use the theme" answer — gets three roles over the line and
;; leaves two under it:
;;
;;   orange-1 / orange-9   3.62:1
;;   green-1  / green-9    3.81:1
;;
;; Mantine's warm and mid hues simply don't have a shade dark enough to sit on
;; their own shade 1 at 4.5:1. The darkest tokens it offers for those hues are
;; the ones above. So the text ends up hand-picked from a darker range while
;; the backgrounds stay pale — five pairs, one place, verified in a browser by
;; `e2e/tests/badge-contrast.spec.ts`.
;;
;; :attention and :active are the two the theme owns, so #798 moved them onto
;; the Klei ramps rather than leaving them one hue behind it — shade 1 under
;; shade 8 in both cases, which clears the floor here even though it doesn't
;; for the Mantine hues above (attention 6.40:1, active 8.20:1).
(def tone->badge-colors
  {:attention {:bg "#fdf1cc" :fg "#943c10"}
   :active    {:bg "#efe7fe" :fg "#511ead"}
   ;; :pending (#891) — in-flight, awaiting an outside event (an issued invoice
   ;; waiting on payment), distinct from :active (being worked). Blue is the
   ;; conventional "open/sent" status colour and keeps this off the brand violet
   ;; :active leans on — violet already marks the interactive/selected surface,
   ;; so a violet status chip competes with it. blue-100 / blue-800 measures
   ;; 7.15:1, well clear of the 4.5 floor `badge-contrast.spec.ts` pins.
   :pending   {:bg "#dbeafe" :fg "#1e40af"}
   ;; :blocked is the tone #679 added — "stuck, and it is on you to unstick it".
   ;; The theme's own red-1 / red-8 measure 6.80:1, in the same band as the
   ;; other four. red-6 (#dc2626) was the obvious pick and fails at 3.95:1 —
   ;; the same trap the note above describes for the warm hues.
   :blocked   {:bg "#fee2e2" :fg "#991b1b"}
   :settled   {:bg "#dcfce7" :fg "#166534"}
   ;; #78716c — the value the #631 handoff specified — measures ~4.35:1 here,
   ;; despite its claim that every pair cleared 4.5.
   :closed    {:bg "#f5f5f4" :fg "#57534e"}})

(def badge-tone-keys (set (keys tone->badge-colors)))

(def ^:private legacy-role->tone
  "#679 renamed :role to :tone and renamed three of its five values. Read the
   old spelling so a definition that has not been migrated keeps its colour
   instead of silently dropping to :closed grey.

   `initial` maps to `attention` deliberately: a brand-new lead is the most
   action-demanding state in the pipeline, not a neutral starting point."
  {:initial          :attention
   :active           :active
   :attention        :attention
   :terminal-success :settled
   :terminal-neutral :closed})

(def ^:private variant->tone
  "Fallback for states that declare no `:tone`, so every aggregate gets the
   accessible palette without having to be migrated first. daskul-basiced
   declares none at all and rides entirely on this."
  {"info"      :attention
   "primary"   :active
   "secondary" :active
   "warning"   :attention
   "success"   :settled
   "danger"    :closed
   "neutral"   :closed})

(defn state-tone
  "The lifecycle tone for a state's UI metadata: an explicit `:tone` wins, then
   a legacy `:role`, then derived from `:variant`, else closed."
  [state-ui]
  (let [declared (some-> (:tone state-ui) keyword)
        legacy   (some-> (:role state-ui) keyword)]
    (or (when (contains? badge-tone-keys declared) declared)
        (get legacy-role->tone legacy)
        (get variant->tone (some-> (:variant state-ui) name))
        :closed)))

(defn badge-colors
  "{:bg :fg} for a state's UI metadata."
  [state-ui]
  (get tone->badge-colors (state-tone state-ui)))

;; The 4.5:1 floor is verified in `e2e/tests/badge-contrast.spec.ts`, which
;; measures the colours a browser actually computes. That catches what a unit
;; test here could not: CSS-variable resolution, theme overrides, and
;; translucent backgrounds composited over whatever they sit on — which is how
;; the old `light` variant hid at ~3:1 in the first place.
